import React, { forwardRef, useImperativeHandle, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import { setValueSpecInfo } from '../../redux/productSlice';
import CustomInput from '../../components/customInput/CustomInput';
import ModalFileUpload from '../../components/ModalFileUpload/ModalFileUpload';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';
import { uploadAsset, deleteAsset } from '../../redux/assetSlice';
import { ASSET_LOCATION_MAPPING, ASSET_TYPE_MAPPING, LABEL_DELETE_ASSET } from '../../utils/constant';
import { isEmpty } from '../../utils/commonUtil';

const SpecInfo = forwardRef(({ language, productId, formType, onShowAlert }, ref) => {
  const dispatch = useDispatch();
  const productState = useSelector((state) => state.product);
  const product = productState.data || {};

  const specInfoData = React.useMemo(() =>
    product.productSpec || product.specInfo || product.contentData?.productSpec || product.contentData?.specInfo || {},
    [product]
  );

  const specInfo = React.useMemo(() =>
    specInfoData.items || [],
    [specInfoData]
  );

  const { title: specTitle = '', prodSpecHeader = '' } = specInfoData;

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [isShowDeleteAsset, setIsShowDeleteAsset] = useState(null);
  const [selectedSpecIndex, setSelectedSpecIndex] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [showDeleteSpecModal, setShowDeleteSpecModal] = useState(false);
  const [specToDelete, setSpecToDelete] = useState(null);

  const PLACEHOLDER_CONFIG = {
    title: { EN: "Enter title EN", CN: "Enter title CN" },
    prodSpecHeader: { EN: "Enter spec header EN", CN: "Enter spec header CN" },
    specTitle: { EN: "Enter sub title EN", CN: "Enter sub title CN" },
    specDescription: { EN: "Enter description EN", CN: "Enter description CN" },
    iconUrl: { EN: "icon.png EN", CN: "icon.png CN" },
    retainSpec: { EN: "At least retain one spec item EN", CN: "At least retain one spec item CN" }
  };

  const INITIAL_SPEC_ITEM = {
    title: '',
    iconUrl: '',
    iconFileId: '',
    description: '',
    width: 72,
    height: 72
  };

  const initialSpecInfoValue = React.useMemo(() => ({
    title: '',
    prodSpecHeader: 'Sell More, Stress Less',
    items: [INITIAL_SPEC_ITEM]
  }), []);

  useEffect(() => {
    const hasValidData = specInfoData && (
      specInfoData.title !== undefined ||
      specInfoData.prodSpecHeader !== undefined ||
      (specInfoData.items && specInfoData.items.length > 0)
    );

    if (!hasValidData) {
      dispatch(setValueSpecInfo({
        value: initialSpecInfoValue
      }));
    }
  }, [dispatch, initialSpecInfoValue, specInfoData]);
  const handleFileUpload = (file) => {
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("uuid", formType === 'EDIT' ? productId : "");
    formData.append("assetLocation", ASSET_LOCATION_MAPPING[selectedField].specInfo);
    formData.append("assetType", ASSET_TYPE_MAPPING.product);
    formData.append("file", file);

    dispatch(uploadAsset(formData))
      .then((res) => {
        if (res?.payload) {
          const fileId = res.payload.resdata.fileId;
          const fileUrl = res.payload.resdata.url;

          dispatch((dispatch, getState) => {
            const state = getState();
            const product = state.product.data || {};
            const specInfoData = product.productSpec || product.contentData?.productSpec || {};
            const currentSpecInfo = specInfoData.items || [];

            const newItems = currentSpecInfo.map((item, i) => {
              if (i === selectedSpecIndex) {
                return {
                  ...item,
                  iconUrl: fileUrl,
                  iconFileId: fileId
                };
              }
              return item;
            });

            dispatch(setValueSpecInfo({
              value: {
                ...specInfoData,
                items: newItems
              }
            }));
          });
        }
        if (res?.error) {
          onShowAlert(res.error.message, false);
        }
      })
      .catch((err) => {
        onShowAlert(err.message || "Upload failed", false);
      })
      .finally(() => {
        setIsUploading(false);
        setShowFileUploadModal(false);
        setSelectedSpecIndex(null);
        setSelectedField(null);
      });
  };

  const handleDeleteFile = () => {
    const fileId = specInfo[selectedSpecIndex]?.iconFileId;

    const newItems = specInfo.map((item, i) => {
      if (i === selectedSpecIndex) {
        return {
          ...item,
          iconUrl: "",
          iconFileId: ""
        };
      }
      return item;
    });

    dispatch(setValueSpecInfo({
      value: {
        ...specInfoData,
        items: newItems
      }
    }));

    if (fileId) {
      dispatch(deleteAsset(fileId))
        .then((res) => {
          if (res?.error &&
            !res.error.message.includes("No active document") &&
            !res.error.message.includes("Unexpected token")) {
            onShowAlert(res.error.message, false);
          }
        })
        .catch((err) => {
          if (err.message &&
            !err.message.includes("No active document") &&
            !err.message.includes("Unexpected token")) {
            onShowAlert(err.message, false);
          }
        })
        .finally(() => {
          setIsShowDeleteAsset(null);
          setSelectedSpecIndex(null);
          setSelectedField(null);
        });
    } else {
      setIsShowDeleteAsset(null);
      setSelectedSpecIndex(null);
      setSelectedField(null);
    }
  };

  const handleShowUploadFile = (specIndex, field) => {
    setSelectedSpecIndex(specIndex);
    setSelectedField(field);
    setShowFileUploadModal(true);
  };

  const handleShowDeleteFile = (specIndex, field) => {
    setSelectedSpecIndex(specIndex);
    setSelectedField(field);
    setIsShowDeleteAsset(LABEL_DELETE_ASSET);
  };

  const handleCancelUploadFile = () => {
    setShowFileUploadModal(false);
    setSelectedSpecIndex(null);
    setSelectedField(null);
  };

  const handleShowDeleteSpec = (index) => {
    if (specInfo.length <= 1) {
      alert(PLACEHOLDER_CONFIG.retainSpec[language]);
      return;
    }
    setSpecToDelete(index);
    setShowDeleteSpecModal(true);
  };

  const handleConfirmDeleteSpec = () => {
    if (specToDelete !== null) {
      dispatch(setValueSpecInfo({
        value: {
          ...specInfoData,
          items: specInfo.filter((_, i) => i !== specToDelete)
        }
      }));
    }
    setShowDeleteSpecModal(false);
    setSpecToDelete(null);
  };

  const addGroup = () => {
    dispatch(setValueSpecInfo({
      value: {
        ...specInfoData,
        items: [...specInfo, INITIAL_SPEC_ITEM]
      }
    }));
  };

  const deleteGroup = (index) => {
    handleShowDeleteSpec(index);
  };

  const updateSpecInfo = (field, value) => {
    dispatch(setValueSpecInfo({
      value: { ...specInfoData, [field]: value }
    }));
  };

  const updateSpecItem = (index, field, value) => {
    dispatch(setValueSpecInfo({
      value: {
        ...specInfoData,
        items: specInfo.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const validateAllSpecInfo = () => ({});

  useImperativeHandle(ref, () => ({
    addGroup,
    validateAllSpecInfo
  }));

  const renderFileUploadSection = (item, index) => (
    <div className='package-input'>
      <label>Icon:</label>
      <input
        type="text"
        placeholder={PLACEHOLDER_CONFIG.iconUrl[language]}
        value={item.iconUrl || ''}
        disabled
        style={{ width: '73%' }}
      />
      {!item.iconUrl ? (
        <button
          className="upload-btn"
          onClick={() => handleShowUploadFile(index, "iconUrl")}
        >
          Upload
        </button>
      ) : (
        <button
          className="icon-btn"
          onClick={() => handleShowDeleteFile(index, "iconUrl")}
        >
          <img src={DeleteIcon} alt="Delete" className="action-icon" />
        </button>
      )}
    </div>
  );

  const renderSpecItem = (item, index) => (
    <div key={index}>
      <div className="domain-group">
        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>Sub Title:</label>
          </div>
          <CustomInput
            type="text"
            value={item.title || ''}
            onChange={(e) => updateSpecItem(index, 'title', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.specTitle[language]}
            customStyle={{ width: '73%' }}
          />
          <button
            className="icon-btn"
            onClick={() => deleteGroup(index)}
          >
            <img src={DeleteIcon} alt="Delete" className="campaign-delete-icon" />
          </button>
        </div>

        {renderFileUploadSection(item, index)}

        {item.iconUrl && (
          <div style={{ marginLeft: '190px', marginBottom: '20px' }}>
            <img
              src={`https://uat.stack-ez.com${item.iconUrl}`}
              alt="Preview"
              style={{ maxWidth: '200px', maxHeight: '200px', marginTop: '10px' }}
            />
          </div>
        )}

        <div className='package-input'>
          <label>Description:</label>
          <textarea
            placeholder={PLACEHOLDER_CONFIG.specDescription[language]}
            value={item.description || ''}
            onChange={(e) => updateSpecItem(index, 'description', e.target.value)}
            rows={3}
            style={{ width: '73%' }}
          />
        </div>

        <div style={{ display: 'flex', gap: '20px', width: '35%' }}>
          <div className='package-input'>
            <label>Width:</label>
            <input
              type="number"
              value={item.width === '' ? '' : (item.width || 72)}
              onChange={(e) => updateSpecItem(index, 'width', e.target.value === '' ? '' : parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '20px', width: '35%' }}>
          <div className='package-input'>
            <label>Height:</label>
            <input
              type="number"
              value={item.height === '' ? '' : (item.height || 72)}
              onChange={(e) => updateSpecItem(index, 'height', e.target.value === '' ? '' : parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>

      {index !== specInfo.length - 1 && (
        <div
          style={{
            height: '0.5px',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            margin: '25px -40px',
            width: 'calc(100% + 80px)',
            display: 'block',
            boxSizing: 'border-box'
          }}
        />
      )}
    </div>
  );

  return (
    <div className="tab-package-container">
      <div className="domain-group">
        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>Title:</label>
          </div>
          <CustomInput
            type="text"
            value={specTitle}
            onChange={(e) => updateSpecInfo('title', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.title[language]}
            customStyle={{ width: '73%' }}
          />
        </div>

        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>Spec Header:</label>
          </div>
          <CustomInput
            type="text"
            value={prodSpecHeader}
            onChange={(e) => updateSpecInfo('prodSpecHeader', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.prodSpecHeader[language]}
            customStyle={{ width: '73%' }}
          />
        </div>
      </div>

      <div
        style={{
          height: '0.5px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          margin: '25px -40px',
          width: 'calc(100% + 80px)',
          display: 'block',
          boxSizing: 'border-box'
        }}
      />

      {specInfo.map(renderSpecItem)}

      <ModalFileUpload
        show={showFileUploadModal}
        handleCancel={handleCancelUploadFile}
        handleUpload={handleFileUpload}
        isLoading={isUploading}
      />

      <CommonDeleteModal
        show={!isEmpty(isShowDeleteAsset)}
        onHide={() => setIsShowDeleteAsset(null)}
        onConfirm={handleDeleteFile}
      />

      <CommonDeleteModal
        show={showDeleteSpecModal}
        onHide={() => {
          setShowDeleteSpecModal(false);
          setSpecToDelete(null);
        }}
        onConfirm={handleConfirmDeleteSpec}
        title="Delete Specification"
        instruction="Are you sure you want to delete this specification item?"
      />
    </div>
  );
});

export default SpecInfo;